import * as THREE from 'three'
import { Noise2D, mulberry32 } from '../core/rng'
import { clamp01, lerp } from '../core/math'

/**
 * The rest of the range.
 *
 * Without this the slope simply stops and dissolves into fog, which reads as
 * "the level ends here" rather than "there is a mountain range out there". Three
 * concentric rings of ridgeline, each further and hazier than the last, give the
 * face something to sit in — and cost three draw calls and a few thousand
 * triangles, all of it beyond the play area and never touched again.
 *
 * Aerial haze is baked into the vertex colours rather than left to scene fog:
 * the fog is tuned for the 900m of slope you actually ski, and anything that
 * works there erases objects a kilometre out entirely.
 */

interface RingSpec {
  radius: number
  /**
   * Summit elevation, above the slope's base.
   *
   * These are deliberately *lower* than the slope's own summit, which is not a
   * mistake. The chase camera pitches ~40° down at the start gate so the player
   * can see the terrain ahead, which puts the horizon off the top of the frame
   * entirely. Peaks tall enough to stand over you therefore have their summits
   * above the frame too, and all you get is a featureless wall of hazy flank
   * with no skyline — mountains you cannot see the top of do not read as
   * mountains. Keeping the range below the camera puts its ridgeline inside the
   * frame, which is also what you actually see from the top of a big face:
   * you look *down* across the surrounding tops.
   */
  peakAbove: number
  /** Vertical spread between summits and cols, metres. */
  relief: number
  /** How far toward the haze colour this ring sits, 0..1. */
  haze: number
  /** Noise frequency around the compass — higher is a busier skyline. */
  roughness: number
  segments: number
}

/**
 * Heights are tuned against the *steepest* camera pitch in the run — the start
 * gate, where the chase cam looks ~40° down and the top of the frame is still
 * 9° below horizontal. A summit has to fall below that line to have sky over
 * it, and because the outer rings are further away they need to be *lower*, not
 * taller, to sit below the same angle. Get that backwards and the far ring
 * quietly fills the top edge and erases the skyline of the ones in front.
 */
const RINGS: readonly RingSpec[] = [
  // Twice as far out as the first version, with half the relief. Halving the
  // height alone barely changed how big the range *looked*, because the visible
  // band is bounded below by the terrain silhouette rather than by the
  // mountains' own feet — shrinking them from the top just moved the skyline
  // down. Distance is the lever that actually reduces apparent size, and it is
  // also what makes a range read as far away rather than as a wall just past
  // the run-out.
  // Distance is capped by the start gate, not by taste. The camera pitches ~40°
  // down there, leaving the top of the frame 8.9° below horizontal, and a range
  // pushed further out rises *toward* the horizon and back off the top of the
  // screen. At 4km the skyline vanished again; ~2.6km is the furthest the
  // outer ring can sit and still show a ridge from the gate.
  { radius: 1750, peakAbove: 78, relief: 105, haze: 0.22, roughness: 3.4, segments: 150 },
  { radius: 2200, peakAbove: 93, relief: 125, haze: 0.44, roughness: 2.5, segments: 130 },
  { radius: 2600, peakAbove: 105, relief: 150, haze: 0.62, roughness: 1.8, segments: 110 },
]

/** Where the ridgelines are rooted, relative to the slope's base elevation. */
const BASE_DROP = 320

export class Backdrop {
  readonly group = new THREE.Group()
  private readonly disposables: Array<THREE.BufferGeometry | THREE.Material> = []
  private readonly materials: THREE.MeshBasicMaterial[] = []
  private highlighted = false

  /**
   * Flood the range with flat magenta. A diagnostic, bound to a key: "I can't
   * see the mountains" is ambiguous between not-drawn, off-screen, and drawn
   * but too low-contrast to register, and those have completely different
   * fixes. One keypress separates them.
   */
  toggleHighlight(): boolean {
    this.highlighted = !this.highlighted
    for (const m of this.materials) {
      m.vertexColors = !this.highlighted
      m.color.set(this.highlighted ? 0xff00c8 : 0xffffff)
      m.needsUpdate = true
    }
    return this.highlighted
  }

  /**
   * @param centre  world position the range is built around
   * @param baseY   elevation of the slope's runout, where the ridges are rooted
   * @param topY    elevation of the slope's summit, which the peaks stand over
   * @param haze    colour distant ridges fade toward — match the fog and sky
   */
  constructor(centre: THREE.Vector3, baseY: number, topY: number, haze: THREE.Color) {
    const rng = mulberry32(0x5eed7ab5)
    const noise = new Noise2D(rng)

    RINGS.forEach((ring, index) => {
      const geo = buildRing(ring, noise, index, baseY, topY, haze)
      const mat = new THREE.MeshBasicMaterial({
        vertexColors: true,
        // Unlit and unfogged: the haze is already in the colours, and scene fog
        // tuned for the slope would swallow these entirely.
        fog: false,
        side: THREE.DoubleSide,
        depthWrite: true,
      })
      const mesh = new THREE.Mesh(geo, mat)
      mesh.position.set(centre.x, 0, centre.z)
      // Drawn early and never occluding anything the player interacts with.
      mesh.renderOrder = -10 + index
      mesh.frustumCulled = false
      mesh.matrixAutoUpdate = false
      mesh.updateMatrix()
      this.group.add(mesh)
      this.materials.push(mat)
      this.disposables.push(geo, mat)
    })
  }

  dispose(): void {
    for (const d of this.disposables) d.dispose()
  }
}

function buildRing(
  ring: RingSpec,
  noise: Noise2D,
  index: number,
  baseY: number,
  topY: number,
  haze: THREE.Color,
): THREE.BufferGeometry {
  const { radius, peakAbove, relief, segments, roughness } = ring
  const bottom = baseY - BASE_DROP
  const summit = baseY + peakAbove
  void topY

  // Two triangles per segment, forming a curtain from the skyline to the base.
  const verts = segments * 6
  const pos = new Float32Array(verts * 3)
  const col = new Float32Array(verts * 3)

  // A distant range reads as a *dark* blue-grey silhouette against a bright
  // sky, with snow catching light only on the highest faces. Built the other
  // way round — white peaks hazed toward a near-white horizon — it technically
  // differs from the sky by 36 levels and still looks like nothing but a pale
  // band of glare, which is exactly what it looked like.
  const peakColour = new THREE.Color(0.93, 0.95, 0.99)
  // Dark enough that the ridgeline actually cuts against the sky. At 0.28 the
  // edge measured 14-24 levels of contrast, which is technically an edge and
  // visually a smudge.
  const rockColour = new THREE.Color(0.17, 0.23, 0.34)
  const tmp = new THREE.Color()

  let o = 0
  const write = (x: number, y: number, z: number, c: THREE.Color): void => {
    pos[o] = x; pos[o + 1] = y; pos[o + 2] = z
    col[o] = c.r; col[o + 1] = c.g; col[o + 2] = c.b
    o += 3
  }

  const skylineAt = (t: number): number => {
    // Sampled on a circle so the profile wraps seamlessly at t = 0 and 1.
    const a = t * Math.PI * 2
    const nx = Math.cos(a) * roughness
    const nz = Math.sin(a) * roughness
    const ridged = 1 - Math.abs(noise.fbm(nx + index * 17, nz - index * 11, 4))
    const lumps = noise.fbm(nx * 2.6 + 40, nz * 2.6 - 25, 3) * 0.5 + 0.5
    const shape = clamp01(ridged * 0.75 + lumps * 0.45)
    // Summits reach `summit`; cols drop a full `relief` below it.
    return summit - relief * (1 - shape)
  }

  for (let s = 0; s < segments; s++) {
    const t0 = s / segments
    const t1 = (s + 1) / segments
    const a0 = t0 * Math.PI * 2
    const a1 = t1 * Math.PI * 2
    const x0 = Math.cos(a0) * radius
    const z0 = Math.sin(a0) * radius
    const x1 = Math.cos(a1) * radius
    const z1 = Math.sin(a1) * radius
    const y0 = skylineAt(t0)
    const y1 = skylineAt(t1)

    // Higher ground is snowier; the flanks fall away to rock.
    // Snow only on the top of the relief, so the mass of the ridge stays dark
    // and the skyline reads as a silhouette rather than a white smear.
    const snowline = (y: number): number =>
      clamp01(((y - (summit - relief)) / Math.max(1, relief)) * 2.4 - 1.25)
    const snow0 = snowline(y0)
    const snow1 = snowline(y1)

    const top0 = tmp.copy(rockColour).lerp(peakColour, snow0).lerp(haze, ring.haze).clone()
    const top1 = tmp.copy(rockColour).lerp(peakColour, snow1).lerp(haze, ring.haze).clone()
    // The base of the curtain sits in valley haze regardless of its peak.
    const foot = tmp.copy(rockColour).lerp(haze, lerp(ring.haze, 1, 0.55)).clone()

    write(x0, y0, z0, top0)
    write(x1, y1, z1, top1)
    write(x0, bottom, z0, foot)

    write(x1, y1, z1, top1)
    write(x1, bottom, z1, foot)
    write(x0, bottom, z0, foot)
  }

  const geo = new THREE.BufferGeometry()
  geo.setAttribute('position', new THREE.BufferAttribute(pos, 3))
  geo.setAttribute('color', new THREE.BufferAttribute(col, 3))
  geo.computeBoundingSphere()
  return geo
}
